import * as Sentry from '@sentry/remix';
import type {
  BasicNode,
  InlineNode,
  TiptapState,
  TPostConditionSetElement,
  TPostContentElement,
  TPostDynamicContentElement,
  TPostSectionElement,
  TPostSubscriberBreak,
  TPostTableCell,
  TPostTableHeader,
  TPostTableRow,
  TRootElement,
} from '@shared/dream-components';
import type { TPostParagraphElement } from '@shared/dream-components/src/postContentElement/components/nodes/paragraph/Paragraph.types';
import type { TPostTable } from '@shared/dream-components/src/postContentElement/components/nodes/table/Table.types';
import type { WritableDraft } from 'immer';
import { produce } from 'immer';

import type { PostMergeTags } from '~/interfaces/post_merge_tags';

const SUBSCRIBER_ID_MERGE_TAG = '{{subscriber_id}}';
const EMAIL_MERGE_TAG = '{{email}}';
const MERGE_TAGS_WITH_HTML = ['rp_personalized_text', 'rp_upcoming_milestones'];

export type ProcessorFunctionOptions = {
  subscriberBreakElementData: TPostSubscriberBreak['attrs']['data'];
  hasSubscription: boolean;
  postMergeTags?: PostMergeTags;
  subscriberId?: string;
  subscriberEmail?: string;
  postTiptapState?: TRootElement | null;
};

export type ProcessorFunction = (
  postContentNode: WritableDraft<TPostContentElement>,
  options?: ProcessorFunctionOptions,
) => void;

export function process(
  postContentNode: TPostContentElement,
  processors: ProcessorFunction[],
  options?: ProcessorFunctionOptions,
): TPostContentElement {
  const processedNodeContent = produce(postContentNode, (draft) => {
    processors.filter(Boolean).forEach((processor) => {
      processor(draft, options);
    });
  });

  return processedNodeContent;
}

const processSubscriberGate = (draft: WritableDraft<TPostContentElement>, options?: ProcessorFunctionOptions) => {
  try {
    if (!Array.isArray(draft.attrs.data.content) || !draft.attrs.data.content.length) {
      return;
    }

    // find the subscriber break node
    const subscriberBreakIndex = draft.attrs.data.content.findIndex(
      (node: BasicNode) => node.type === 'subscriberBreak',
    );

    // there is no subscriber break
    if (subscriberBreakIndex === -1) return;

    // Inject data required to render Subscriber break element
    draft.attrs.data.content[subscriberBreakIndex] = {
      ...draft.attrs.data.content[subscriberBreakIndex],
      attrs: {
        data: {
          ...(options?.subscriberBreakElementData ? { ...options.subscriberBreakElementData } : {}),
        },
      },
    };

    // remove all contents after the subscriber break
    draft.attrs.data.content = draft.attrs.data.content.slice(0, subscriberBreakIndex + 1);
  } catch (error) {
    Sentry.captureException(error);
  }
};

// Example {{foo}} {{foo bar}} {{foo|bar}} {{ foo | bar }} {{ foo bar | baz buz }}
const MERGE_TAG_PATTERN = /\{\{\s*([^|}]+?)\s*(?:\|\s*([^|}]+?)\s*)?\}\}/g;

/**
 * Recursively walks an object/array tree and replaces merge tags in string values.
 * This approach is safer than JSON.stringify/parse because it:
 * - Avoids JSON parsing errors from user content
 * - Only replaces in actual string values, not in JSON structure
 * - Handles deeply nested objects correctly
 */
const replaceMergeTagsInValue = (value: unknown, mergeTags: PostMergeTags): unknown => {
  // Handle strings - this is where merge tag replacement happens
  if (typeof value === 'string') {
    let result = value;

    // Find all merge tags in the string
    const foundMergeTags = result.match(MERGE_TAG_PATTERN);
    if (!foundMergeTags) {
      return result;
    }

    foundMergeTags.forEach((tag) => {
      // Simple merge tag (example: {{subscriber_id}})
      // biome-ignore lint/suspicious/noPrototypeBuiltins: Need to check own properties safely
      if (Object.prototype.hasOwnProperty.call(mergeTags, tag)) {
        result = result.replaceAll(tag, mergeTags[tag]);
        return;
      }

      // Merge tags with default value (example: {{first_name|Guest}})
      const isMergeTagWithDefaultValue = tag.includes('|');
      if (isMergeTagWithDefaultValue) {
        try {
          const [tagName, tagDefaultValue] = tag.replace(/^\{\{|\}\}$/g, '').split('|');
          const tagNameWithoutSpaces = tagName.replaceAll(' ', '');
          const replacementValue = mergeTags[`{{${tagNameWithoutSpaces}}}`] || tagDefaultValue.trimStart().trimEnd();
          result = result.replaceAll(tag, replacementValue);
        } catch {
          // Fail gracefully for malformed default value syntax
        }
        return;
      }

      // Tag with spaces (example: {{ subscriber_id }})
      const extractedTagName = tag.replace(/^\{\{|\}\}$/g, '').trim();
      if (mergeTags[`{{${extractedTagName}}}`]) {
        result = result.replaceAll(tag, mergeTags[`{{${extractedTagName}}}`]);
      }
    });

    return result;
  }

  // Handle arrays - recurse into each element
  if (Array.isArray(value)) {
    return value.map((item) => replaceMergeTagsInValue(item, mergeTags));
  }

  // Handle objects - recurse into each property
  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      result[key] = replaceMergeTagsInValue(val, mergeTags);
    }
    return result;
  }

  // Primitives (numbers, booleans, null) - return as-is
  return value;
};

const processMergeTags = (draft: WritableDraft<TPostContentElement>, options?: ProcessorFunctionOptions) => {
  if (!Array.isArray(draft.attrs.data.content) || !draft.attrs.data.content.length) {
    return;
  }

  try {
    // Default merge tags, combined with any additional merge tags provided in options
    const mergeTags: PostMergeTags = {
      [SUBSCRIBER_ID_MERGE_TAG]: options?.subscriberId || SUBSCRIBER_ID_MERGE_TAG,
      [EMAIL_MERGE_TAG]: options?.subscriberEmail || EMAIL_MERGE_TAG,
      ...(options?.postMergeTags || {}),
    };

    // Use object-walking approach instead of JSON.stringify/parse [WEB-5442]
    // This is safer because it replaces values directly in the object tree,
    // avoiding JSON parsing errors from user-provided content
    draft.attrs.data.content = produce(draft.attrs.data.content, (contentDraft: TiptapState) => {
      return replaceMergeTagsInValue(contentDraft, mergeTags) as TiptapState;
    });
  } catch (error) {
    // Capture detailed context for debugging if the new approach still fails [WEB-5442]
    Sentry.withScope((scope) => {
      scope.setContext('merge_tags_error', {
        error_message: error instanceof Error ? error.message : String(error),
        content_structure: JSON.stringify(draft.attrs.data.content, null, 2).substring(0, 1000),
        merge_tags: JSON.stringify(options?.postMergeTags || {}, null, 2).substring(0, 500),
        subscriber_id: options?.subscriberId,
        subscriber_email: options?.subscriberEmail,
      });
      Sentry.captureException(error);
    });

    // Re-throw to maintain existing error behavior
    throw error;
  }
};

const processNodeRecursively = (node: any, options?: ProcessorFunctionOptions): any => {
  switch (node.type) {
    case 'dynamicContent':
    case 'conditionSet':
    case 'table':
    case 'tableRow':
    case 'tableCell':
    case 'tableHeader':
    case 'section':
      return produce(
        node,
        (
          draftNode:
            | TPostDynamicContentElement
            | TPostConditionSetElement
            | TPostTable
            | TPostTableRow
            | TPostTableCell
            | TPostTableHeader
            | TPostSectionElement,
        ) => {
          if (draftNode.content) {
            draftNode.content = recursivelyProcessNodeContent(draftNode.content, options);
          }

          return draftNode;
        },
      );
    case 'paragraph':
      return processParagraphNode(node);
    default:
      return node;
  }
};

const recursivelyProcessNodeContent = (nodeContent: any[], options?: ProcessorFunctionOptions): any => {
  if (!Array.isArray(nodeContent) || nodeContent.length === 0) {
    return nodeContent;
  }

  return nodeContent.map((childNode: any) => processNodeRecursively(childNode, options)).filter(Boolean);
};

const processData = (draft: WritableDraft<TPostContentElement>, options?: ProcessorFunctionOptions) => {
  if (Array.isArray(options?.postTiptapState?.content) && options?.postTiptapState?.content.length > 0) {
    draft.attrs.data.content = produce(options?.postTiptapState.content, (draftContent: TiptapState) => {
      return draftContent.map((node: any) => processNodeRecursively(node, options)).filter(Boolean);
    });
    return;
  }

  if (Array.isArray(draft.attrs.data.content) && draft.attrs.data.content.length > 0) {
    draft.attrs.data.content = produce(draft.attrs.data.content, (draftContent: TiptapState) => {
      return draftContent.map((node: any) => processNodeRecursively(node, options)).filter(Boolean);
    });
  }
};

const encodeHtmlTagsInText = (text: string) =>
  text.replace(/<\/?[a-zA-Z][^>]*>/g, (tag) => tag.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'));

const processParagraphNode = (node: TPostParagraphElement) => {
  return produce(node, (draftNode) => {
    if (!draftNode.content) {
      return draftNode;
    }

    draftNode.content = produce(draftNode.content, (draftContent) => {
      return draftContent?.map((inlineNode) => {
        if ('text' in inlineNode && !!inlineNode.text) {
          const hasMergeTagWithHtml = MERGE_TAGS_WITH_HTML.some((tag) => inlineNode.text.includes(tag));
          return {
            ...inlineNode,
            text: hasMergeTagWithHtml ? encodeHtmlTagsInText(inlineNode.text) : inlineNode.text,
            renderAsHtml: hasMergeTagWithHtml,
          };
        }
        return inlineNode;
      }) as InlineNode[];
    });

    return draftNode;
  });
};

export const processors = {
  processSubscriberGate,
  processMergeTags,
  processData,
  processParagraphNode,
};
